import { ErrorCode } from './ErrorCode'
import { AppError } from './AppError'
import {
  isHttpError,
  isProblemDetails,
  type HttpError,
  type ProblemDetails
} from './http-error-types'

function mapHttpError(error: HttpError): AppError {
  const body = error.data
  const problemDetails: ProblemDetails | undefined = isProblemDetails(body)
    ? body
    : undefined

  const message =
    problemDetails?.friendlyTitle ??
    problemDetails?.title ??
    error.message ??
    'Request failed'

  // `validationErrors` is deliberately duplicated into `details` (raw blob
  // via the spread, extracted field via top-level). The top-level field is
  // the ergonomic API for form handlers; `details.errors` preserves the
  // full backend payload for logs and debugging without a separate lookup.
  const details: Record<string, unknown> | undefined = problemDetails
    ? { ...problemDetails }
    : body && typeof body === 'object'
      ? (body as Record<string, unknown>)
      : undefined

  return new AppError({
    message,
    code: ErrorCode.UNEXPECTED,
    statusCode: error.status,
    traceId: problemDetails?.traceId ?? problemDetails?.instance,
    validationErrors: problemDetails?.errors,
    details
  })
}

export function mapToAppError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error
  }
  if (isHttpError(error)) {
    return mapHttpError(error)
  }
  if (error instanceof Error) {
    // If the error wraps something meaningful in `cause`, unwrap it so
    // status / traceId / validationErrors aren't buried where nobody reads
    // them. Happens e.g. when a lower-level boundary re-throws with
    // `new Error(msg, { cause })` and that cause carries a genuine
    // HTTP response or an AppError that was already mapped upstream.
    if (error.cause instanceof AppError) {
      return error.cause
    }
    if (isHttpError(error.cause)) {
      return mapHttpError(error.cause)
    }
    return new AppError({
      code: ErrorCode.UNEXPECTED,
      message: error.message,
      details:
        error.cause != null
          ? {
              cause:
                error.cause instanceof Error ? error.cause.message : error.cause
            }
          : undefined
    })
  }
  return new AppError({
    code: ErrorCode.UNEXPECTED,
    message: 'An unexpected error occurred'
  })
}
